import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import socioService from "../services/socioService.tsx";
import LogoCamara from "../assets/LogoCamara.png";
import { Heart, Lock, Search, Star, Store, Tag } from "lucide-react";
import Loading from "./Loading.tsx";
import { useAuth } from "../context/AuthProvider.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import { MVP_PRODUCTOS, type ProductoMvp } from "../data/mvpProducts.ts";
import { createComercioMarket, getComerciosMarket, type ComercioStore } from "../services/marketplaceService.tsx";
import { getComerciosResumen } from "../services/puntosAdminService.tsx";

type ComercioAdherido = ComercioStore;

const CATEGORIAS = [
  "Hecho en Bolívar",
  "Emprendedores locales",
  "Ofertas",
  "Tecnología",
  "Hogar",
  "Gastronomía",
  "Servicios",
  "Industria local",
] as const;

type Categoria = (typeof CATEGORIAS)[number];

const EMPRENDEDORES_LOCALES = [
  "GastroBol",
  "Electro Sur",
  "PagosPyme",
  "Muebles Bolívar",
  "Tecno Punto",
  "Control House",
];

const HECHO_EN_BOLIVAR = [
  "GastroBol",
  "Muebles Bolívar",
  "Control House",
];

const esEmprendedorLocal = (nombre: string) =>
  EMPRENDEDORES_LOCALES.some((n) => n.toLowerCase() === String(nombre || "").toLowerCase());

const esHechoEnBolivar = (nombre: string) =>
  HECHO_EN_BOLIVAR.some((n) => n.toLowerCase() === String(nombre || "").toLowerCase());

export default function LoginRequired() {
  const { isAuthenticated, user, isAdmin } = useAuth();
  const { loginWithRedirect } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoriaActiva, setCategoriaActiva] = useState<Categoria>("Hecho en Bolívar");
  const [comerciosAdheridos, setComerciosAdheridos] = useState<ComercioAdherido[]>([]);
  const [loadingComercios, setLoadingComercios] = useState(true);
  const [favoritos, setFavoritos] = useState<(string | number)[]>([]);
  const [logosRotos, setLogosRotos] = useState<Record<string, boolean>>({});
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const favoritosKey = useMemo(() => {
    const userKey = user?.email || user?.sub || "anon";
    return `market-favoritos:${userKey}`;
  }, [user]);

  const login = () => {
    setLoading(true);
    try {
      loginWithRedirect();
    } catch (error) {
      console.error("Error en el login:", error);
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    if (isAuthenticated && user) {
      try {
        setLoading(true);
        const data = await socioService.getUserProfile(`${user.email}`, getAccessTokenSilently);

        if (data && data.length > 0) {
          socioService.saveSociosInLocalStorage(data);

          if (data.length === 1) {
            socioService.saveSelectedSocioInLocalStorage(data[0]);
          }
        }
      } catch (error) {
        console.error("Error al obtener los datos del socio:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated && user) {
        if (!isAdmin) {
          try {
            await fetchUserData();
          } catch (err) {
            console.error("Error al traer datos del usuario:", err);
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      }
    };

    checkAuth();
  }, [isAuthenticated, user]);

  useEffect(() => {
    const loadComercios = async () => {
      try {
        setLoadingComercios(true);
        const market = (await getComerciosMarket()) as ComercioAdherido[];
        let combinados: ComercioAdherido[] = Array.isArray(market) ? [...market] : [];

        if (isAuthenticated) {
          try {
            const token = await getAccessTokenSilently();
            const puntos = await getComerciosResumen(token);

            const byNombre = new Map(
              combinados.map((c) => [String(c?.nombre || "").trim().toLowerCase(), c])
            );

            for (const p of Array.isArray(puntos) ? puntos : []) {
              const nombre = String((p as any)?.nombre || "").trim();
              if (!nombre) continue;
              const key = nombre.toLowerCase();
              if (byNombre.has(key)) continue;

              const puntoLike: ComercioAdherido = {
                comercio_id: Number((p as any)?.comercio_id || 0),
                socio_id: 0,
                nombre,
                slug: "",
                logo_url: (p as any)?.logo_url || undefined,
                activo: Boolean((p as any)?.activo ?? true),
                es_emprendedor: false,
              };

              combinados.push(puntoLike);
              byNombre.set(key, puntoLike);
            }
          } catch (err) {
            console.warn("No se pudieron cargar comercios de socios (puntos)", err);
          }
        }

        setComerciosAdheridos(combinados);
      } catch {
        setComerciosAdheridos([]);
      } finally {
        setLoadingComercios(false);
      }
    };

    loadComercios();
  }, [isAuthenticated, getAccessTokenSilently]);

  const productos = useMemo(() => {
    const q = search.trim().toLowerCase();

    let base = MVP_PRODUCTOS;
    if (categoriaActiva === "Emprendedores locales") {
      base = MVP_PRODUCTOS.filter((p) => esEmprendedorLocal(p.comercio));
    }
    if (categoriaActiva === "Hecho en Bolívar") {
      base = MVP_PRODUCTOS.filter((p) => esHechoEnBolivar(p.comercio));
    }

    if (!q) return base;
    return base.filter((p) => `${p.nombre} ${p.comercio}`.toLowerCase().includes(q));
  }, [search, categoriaActiva]);

  const comerciosFiltrados = useMemo(() => {
    if (categoriaActiva === "Hecho en Bolívar") {
      const bolivar = comerciosAdheridos.filter((c) => esHechoEnBolivar(c.nombre));
      return bolivar.length ? bolivar : comerciosAdheridos;
    }
    return comerciosAdheridos;
  }, [comerciosAdheridos, categoriaActiva]);

  const comerciosOficiales = useMemo(
    () => comerciosFiltrados.filter((c) => !(c.es_emprendedor || c.emprendedor?.tipo === "emprendedor")),
    [comerciosFiltrados]
  );

  const comerciosEmprendedores = useMemo(
    () => comerciosFiltrados.filter((c) => c.es_emprendedor || c.emprendedor?.tipo === "emprendedor"),
    [comerciosFiltrados]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setFavoritos([]);
      return;
    }
    try {
      const raw = localStorage.getItem(favoritosKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setFavoritos(Array.isArray(parsed) ? parsed : []);
    } catch {
      setFavoritos([]);
    }
  }, [isAuthenticated, favoritosKey]);

  const toggleFavorito = (productoId: string | number) => {
    if (!isAuthenticated) return;
    setFavoritos((prev) => {
      const exists = prev.some((id) => String(id) === String(productoId));
      const next = exists
        ? prev.filter((id) => String(id) !== String(productoId))
        : [...prev, productoId];
      localStorage.setItem(favoritosKey, JSON.stringify(next));
      return next;
    });
  };

  const esFavorito = (productoId: string | number) =>
    favoritos.some((id) => String(id) === String(productoId));

  const handleOpenComercio = async (c: ComercioAdherido) => {
    if (c.slug) {
      navigate(`/tienda/${c.slug}`);
      return;
    }

    if (!isAuthenticated) return;

    try {
      const socioSel = localStorage.getItem("socioSeleccionado");
      const socioParsed = socioSel ? JSON.parse(socioSel) : null;
      const socioId = Number(socioParsed?.socio_id);
      if (!Number.isFinite(socioId) || socioId <= 0) {
        alert("No encontramos el socio seleccionado para abrir la tienda.");
        return;
      }

      const created = await createComercioMarket({
        nombre: c.nombre,
        socio_id: socioId,
        descripcion: `Comercio asociado desde catálogo: ${c.nombre}`,
        activo: true,
      });

      if ((created as any)?.slug) {
        setComerciosAdheridos((prev) =>
          prev.map((item) =>
            String(item.nombre || "").trim().toLowerCase() === String(c.nombre || "").trim().toLowerCase()
              ? { ...item, slug: (created as any).slug, comercio_id: (created as any).comercio_id || item.comercio_id }
              : item
          )
        );
        navigate(`/tienda/${(created as any).slug}`);
      }
    } catch (error) {
      console.error("No se pudo abrir/crear tienda para el comercio", error);
      alert("Este comercio todavía no tiene tienda publicada.");
    }
  };

  const logoKey = (c: ComercioAdherido, idx: number) => String(c.comercio_id || `${c.nombre}-${idx}`);
  const isLogoVisible = (c: ComercioAdherido, idx: number) => {
    const key = logoKey(c, idx);
    return Boolean(c.logo_url) && !logosRotos[key];
  };

  if (isAuthenticated && loading) {
    return <Loading />;
  }

  return (
      <div className="min-h-screen bg-[#eef6ff]">
        <header className="sticky top-0 z-20 border-b border-sky-200 bg-white/95 backdrop-blur">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-3 px-4 py-3 md:grid-cols-[300px_minmax(340px,1fr)_280px] sm:px-6">
            <div className="flex items-center gap-2">
              <img src={LogoCamara} alt="Camara Comercial Bolívar" className="h-20 w-20 rounded-lg border border-sky-100 bg-white object-contain p-1" />
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-sky-600">Marketplace Socios</p>
                <h1 className="text-sm font-bold text-slate-800 sm:text-base">Cámara Comercial de Bolívar</h1>
              </div>
            </div>

            <div className="flex w-full max-w-xl items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-1.5 shadow-sm">
              <Search className="h-4 w-4 text-sky-600" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar productos, marcas y comercios..."
                className="w-full bg-transparent text-sm text-slate-700 outline-none"
              />
            </div>

            <div className="ml-auto flex items-center justify-end gap-2">
              <div className="inline-flex items-center gap-1 rounded-lg border border-sky-200 bg-white px-3 py-2 text-xs font-semibold text-sky-700 sm:text-sm">
                <Heart className="h-4 w-4" />
                Favoritos {isAuthenticated ? `(${favoritos.length})` : ""}
              </div>
              {!isAuthenticated ? (
                <button
                  onClick={() => login()}
                  className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-xs font-semibold text-white shadow hover:bg-sky-700 sm:text-sm"
                >
                  <Lock className="h-4 w-4" />
                  Ingresar
                </button>
              ) : (
                <details className="relative">
                  <summary className="list-none cursor-pointer rounded-lg border border-sky-200 bg-white px-3 py-2 text-right">
                    <p className="max-w-[220px] truncate text-xs font-semibold text-slate-800">Hola, {user?.name || "Usuario"}</p>
                    <p className="max-w-[220px] truncate text-[11px] text-slate-500">{user?.email || ""}</p>
                  </summary>
                  <div className="absolute right-0 z-30 mt-2 w-52 rounded-xl border border-sky-200 bg-white p-2 shadow-lg">
                    <button
                      onClick={() => navigate(isAdmin ? "/admin" : "/inicio")}
                      className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-sky-50"
                    >
                      Ir a mi panel
                    </button>
                  </div>
                </details>
              )}
            </div>
          </div>

          <div className="mx-auto flex w-full max-w-7xl gap-2 overflow-x-auto px-4 pb-3 sm:px-6">
            {CATEGORIAS.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoriaActiva(cat)}
                className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition ${
                  categoriaActiva === cat
                    ? 'border-sky-600 bg-sky-600 text-white'
                    : 'border-sky-200 bg-white text-sky-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl px-4 py-5 sm:px-6">
          {!search.trim() && (
            <>
              {categoriaActiva === 'Hecho en Bolívar' ? (
                <section className="mb-5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white shadow-lg">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-100">Hecho en Bolívar</p>
                  <h2 className="mt-1 text-3xl font-extrabold leading-tight">Estás impulsando el comercio local</h2>
                  <p className="mt-2 text-sm text-emerald-50">
                    Elegí productos y tiendas que producen en Bolívar. Comprando acá fortalecés empleo local y cadena productiva de la ciudad.
                  </p>
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="rounded-xl bg-white/15 p-3 text-sm backdrop-blur">
                      <p className="font-bold">Envío bonificado</p>
                      <p className="text-emerald-100">En productos seleccionados, el costo de envío corre por nuestra cuenta.</p>
                    </div>
                    <div className="rounded-xl bg-white/15 p-3 text-sm backdrop-blur">
                      <p className="font-bold">Entrega en el día</p>
                      <p className="text-emerald-100">En zona urbana, te puede llegar en el transcurso del día.</p>
                    </div>
                    <div className="rounded-xl bg-white/15 p-3 text-sm backdrop-blur">
                      <p className="font-bold">Beneficio local</p>
                      <p className="text-emerald-100">Sello diferencial frente a otras categorías del marketplace.</p>
                    </div>
                  </div>
                </section>
              ) : (
                <>
                  <section className="mb-4 grid gap-3 md:grid-cols-3">
                    <div className="rounded-xl border border-sky-200 bg-white p-3 text-sm shadow-sm"><b className="text-sky-700">Envíos</b> en Bolívar y zona</div>
                    <div className="rounded-xl border border-sky-200 bg-white p-3 text-sm shadow-sm"><b className="text-sky-700">Hasta 12 cuotas</b> en comercios adheridos</div>
                    <div className="rounded-xl border border-sky-200 bg-white p-3 text-sm shadow-sm"><b className="text-sky-700">Promos semanales</b> exclusivas para socios</div>
                  </section>

                  <section className="mb-5 grid gap-4 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-700 p-5 text-white md:grid-cols-[1.3fr_1fr]">
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-sky-100">MVP · Catálogo digital principal</p>
                      <h2 className="text-2xl font-extrabold leading-tight sm:text-3xl">Comprá a socios locales en un solo lugar</h2>
                      <p className="mt-2 text-sm text-sky-100">
                        Demo visual tipo mercado online, con estética celeste/blanco del sistema. En la versión final cada socio podrá cargar sus productos y publicar promos.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                      <div className="rounded-xl bg-white/15 p-3 backdrop-blur">
                        <p className="text-sky-100">Comercios activos</p>
                        <p className="text-xl font-bold">128</p>
                      </div>
                      <div className="rounded-xl bg-white/15 p-3 backdrop-blur">
                        <p className="text-sky-100">Productos publicados</p>
                        <p className="text-xl font-bold">2.430</p>
                      </div>
                      <div className="rounded-xl bg-white/15 p-3 backdrop-blur">
                        <p className="text-sky-100">Ofertas del día</p>
                        <p className="text-xl font-bold">48</p>
                      </div>
                      <div className="rounded-xl bg-white/15 p-3 backdrop-blur">
                        <p className="text-sky-100">Entrega local</p>
                        <p className="text-xl font-bold">24h</p>
                      </div>
                    </div>
                  </section>

                  <section className="mb-5 rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-600 to-violet-600 p-5 text-white shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-100">Nuevo</p>
                    <h3 className="mt-1 text-2xl font-extrabold">¿Vendés desde casa, feria o redes?</h3>
                    <p className="mt-2 text-sm text-indigo-100">
                      Sumate al registro de emprendedores de Bolívar sin trámites complicados. Creá tu perfil y publicá con ayuda de IA.
                    </p>
                    <button
                      onClick={() => navigate('/emprendedores')}
                      className="mt-3 rounded-lg bg-white px-4 py-2 text-sm font-bold text-indigo-700 hover:bg-indigo-50"
                    >
                      Quiero empezar a vender
                    </button>
                  </section>
                </>
              )}

              <section className="mb-5 rounded-2xl border border-sky-100 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-800">
                {categoriaActiva === 'Emprendedores locales'
                  ? 'Tiendas de emprendedores locales'
                  : categoriaActiva === 'Hecho en Bolívar'
                    ? 'Tiendas con sello Hecho en Bolívar'
                    : 'Comercios adheridos'}
              </h3>
              <span className="text-xs font-semibold text-sky-700">{comerciosFiltrados.length} visibles</span>
            </div>
            {loadingComercios ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={`sk-com-${i}`} className="flex items-center gap-3 rounded-xl border border-sky-100 bg-sky-50 p-3 animate-pulse">
                    <div className="h-14 w-14 rounded-lg bg-sky-100" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-4/5 rounded bg-sky-100" />
                      <div className="h-2 w-2/5 rounded bg-sky-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : comerciosFiltrados.length === 0 ? (
              <p className="rounded-xl border border-dashed border-sky-200 bg-sky-50 p-4 text-sm text-slate-600">
                No hay comercios adheridos activos para mostrar todavía.
              </p>
            ) : (
              <div className="space-y-4">
                {comerciosOficiales.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-sky-700">Tiendas oficiales de socios</p>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                      {comerciosOficiales.map((c, idx) => (
                        <button
                          key={`${c.comercio_id || c.nombre}-${idx}`}
                          onClick={() => handleOpenComercio(c)}
                          className="flex items-center gap-3 rounded-xl border border-sky-100 bg-sky-50 p-3 text-left"
                        >
                          {isLogoVisible(c, idx) ? (
                            <img
                              src={c.logo_url}
                              alt={c.nombre}
                              className="h-14 w-14 rounded-lg border border-sky-100 bg-white object-cover"
                              onError={() => setLogosRotos((prev) => ({ ...prev, [logoKey(c, idx)]: true }))}
                            />
                          ) : (
                            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-white text-base font-extrabold text-sky-600">
                              {c.nombre.slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="line-clamp-2 text-sm font-semibold text-slate-700">{c.nombre}</p>
                            <span className="mt-1 inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-700">
                              Tienda oficial
                            </span>
                            {esHechoEnBolivar(c.nombre) && (
                              <span className="mt-1 ml-1 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                                Hecho en Bolívar
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {comerciosEmprendedores.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-violet-700">Emprendedores locales (venta desde casa/redes)</p>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                      {comerciosEmprendedores.map((c, idx) => (
                        <button
                          key={`${c.comercio_id || c.nombre}-${idx}`}
                          onClick={() => handleOpenComercio(c)}
                          className="flex items-center gap-3 rounded-xl border border-violet-100 bg-violet-50 p-3 text-left"
                        >
                          {isLogoVisible(c, idx) ? (
                            <img
                              src={c.logo_url}
                              alt={c.nombre}
                              className="h-14 w-14 rounded-lg border border-violet-100 bg-white object-cover"
                              onError={() => setLogosRotos((prev) => ({ ...prev, [logoKey(c, idx)]: true }))}
                            />
                          ) : (
                            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-white text-base font-extrabold text-violet-600">
                              {c.nombre.slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="line-clamp-2 text-sm font-semibold text-slate-700">{c.nombre}</p>
                            <span className="mt-1 inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">
                              Emprendedor local
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
            </>
          )}

          {search.trim() && (
            <p className="mb-3 text-sm font-semibold text-slate-700">
              Se encontraron <span className="text-sky-700">{productos.length}</span> producto(s)
            </p>
          )}

          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800">
              {search.trim()
                ? `Resultados para "${search.trim()}"`
                : categoriaActiva === 'Hecho en Bolívar'
                  ? 'Productos con sello Hecho en Bolívar'
                  : categoriaActiva === 'Emprendedores locales'
                    ? 'Productos de emprendedores locales'
                    : 'Productos destacados'}
            </h3>
            <button
              onClick={() => navigate("/institucional")}
              className="text-sm font-semibold text-sky-700 hover:text-sky-900"
            >
              Conocé la Cámara
            </button>
          </div>

          {search.trim() ? (
            <section className="space-y-3">
              {productos.length === 0 ? (
                <div className="rounded-xl border border-dashed border-sky-200 bg-white p-5 text-sm text-slate-600">
                  No encontramos productos para tu búsqueda.
                </div>
              ) : (
                productos.map((p) => (
                  <article key={p.id} className="grid grid-cols-[110px_1fr_auto] gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="relative flex h-24 w-full items-center justify-center rounded-lg bg-sky-50">
                      {p.tag ? (
                        <span className="absolute left-1 top-1 rounded bg-sky-600 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">{p.tag}</span>
                      ) : null}
                      {isAuthenticated && (
                        <button
                          onClick={() => toggleFavorito(p.id)}
                          className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-slate-500 shadow-sm hover:text-rose-600"
                          title={esFavorito(p.id) ? "Quitar de favoritos" : "Agregar a favoritos"}
                        >
                          <Heart className={`h-4 w-4 ${esFavorito(p.id) ? "fill-rose-500 text-rose-500" : ""}`} />
                        </button>
                      )}
                      <Store className="h-9 w-9 text-sky-300" />
                    </div>
                    <div>
                      <h4 className="line-clamp-2 text-sm font-semibold text-slate-800">{p.nombre}</h4>
                      <p className="mt-0.5 text-xs text-slate-500">Vendido por {p.comercio}</p>
                      {esHechoEnBolivar(p.comercio) && (
                        <span className="mt-1 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                          Hecho en Bolívar
                        </span>
                      )}
                      <p className="mt-1 text-2xl font-extrabold text-slate-900">{p.precio}</p>
                      <p className="text-xs font-medium text-sky-700">{p.cuota}</p>
                      <div className="mt-1 flex items-center gap-3 text-xs">
                        <span className="inline-flex items-center gap-1 text-amber-500"><Star className="h-4 w-4 fill-current" /> {p.rating}</span>
                        <span className="font-semibold text-emerald-600">{p.envio}</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <button
                        onClick={() => navigate(`/producto/${p.id}`)}
                        className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-700"
                      >
                        <Tag className="h-4 w-4" /> Ver
                      </button>
                    </div>
                  </article>
                ))
              )}
            </section>
          ) : (
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {productos.map((p) => (
                <article key={p.id} className="rounded-2xl border border-sky-100 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
                  <div className="relative mb-3 flex h-44 items-center justify-center rounded-xl bg-sky-50">
                    {p.tag ? (
                      <span className="absolute left-2 top-2 rounded-md bg-sky-600 px-2 py-1 text-[10px] font-bold uppercase text-white">{p.tag}</span>
                    ) : null}
                    {isAuthenticated && (
                      <button
                        onClick={() => toggleFavorito(p.id)}
                        className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-slate-500 shadow-sm hover:text-rose-600"
                        title={esFavorito(p.id) ? "Quitar de favoritos" : "Agregar a favoritos"}
                      >
                        <Heart className={`h-4 w-4 ${esFavorito(p.id) ? "fill-rose-500 text-rose-500" : ""}`} />
                      </button>
                    )}
                    <Store className="h-14 w-14 text-sky-300" />
                  </div>

                  <h4 className="line-clamp-2 text-sm font-semibold text-slate-800">{p.nombre}</h4>
                  <p className="mt-1 text-xs text-slate-500">Vendido por {p.comercio}</p>
                  {esHechoEnBolivar(p.comercio) && (
                    <span className="mt-1 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      Hecho en Bolívar
                    </span>
                  )}

                  <div className="mt-3">
                    <p className="text-2xl font-extrabold text-slate-900">{p.precio}</p>
                    <p className="text-xs font-medium text-sky-700">{p.cuota}</p>
                  </div>

                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1 text-amber-500">
                      <Star className="h-4 w-4 fill-current" /> {p.rating}
                    </span>
                    <span className="font-semibold text-emerald-600">{p.envio}</span>
                  </div>

                  <button
                    onClick={() => navigate(`/producto/${p.id}`)}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
                  >
                    <Tag className="h-4 w-4" /> Ver producto
                  </button>
                </article>
              ))}
            </section>
          )}
        </main>
      </div>
    );
}
